import { Wrapper } from '@american-autonomy/ag-ui';

import Modal from 'components/Modal';

import PanelActionFooter from 'pages/MaintenancePage/components/PanelActionFooter';
import { getManufacturerDefaults } from 'pages/MaintenancePage/helpers/getManufacturerDefaults';
import { getModelParts } from 'pages/MaintenancePage/helpers/getModelParts';
import useTranslation from 'pages/MaintenancePage/hooks/useTranslation';

import useIntervalDraft from './hooks/useIntervalDraft';

import IntervalConfigRows from './components/IntervalConfigRows';
import ModelSelector from './components/ModelSelector';
import {
    FOOTER_CANCEL_BUTTON_TEST_ID,
    FOOTER_SAVE_BUTTON_TEST_ID,
    FOOTER_TEST_ID,
    HELPER_TEXT_TEST_ID,
    TEST_ID
} from './constants';
import { getModelLabels } from './helpers/getModelLabels';
import { getPartLabels } from './helpers/getPartLabels';
import { getUnitLabels } from './helpers/getUnitLabels';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const ServiceIntervalConfigPanel: FunctionComponent<Props> = ({
    className = '',
    configModel,
    isAdmin,
    isOpen,
    onClose,
    onModelChange,
    onSave,
    serviceIntervals,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const {
        draftIntervals,
        handleChangeDraft,
        handleChangeDraftUnit,
        handleResetDraft,
        isDraftValid
    } = useIntervalDraft({ isOpen, model: configModel, serviceIntervals });

    const handleSave = () => onSave({ intervals: draftIntervals });

    const footer = (
        <PanelActionFooter
            cancelTestId={FOOTER_CANCEL_BUTTON_TEST_ID}
            cancelText={t.INTERVAL_CONFIG_CANCEL}
            isPrimaryDisabled={!isDraftValid}
            onCancel={onClose}
            onPrimary={handleSave}
            primaryTestId={FOOTER_SAVE_BUTTON_TEST_ID}
            primaryText={t.INTERVAL_CONFIG_SAVE}
            testId={FOOTER_TEST_ID}
        />
    );

    return (
        <Modal
            className={`${styles.configModal} ${className}`}
            footer={footer}
            isOpen={isOpen}
            onClose={onClose}
            testId={testId}
            title={t.INTERVAL_CONFIG_TITLE}>
            <ModelSelector
                className={styles.modelSelector}
                label={t.MODEL_SELECTOR_LABEL}
                modelLabels={getModelLabels(t)}
                onSelect={onModelChange}
                selectedModel={configModel}
            />

            <Wrapper className={styles.helperText} testId={HELPER_TEXT_TEST_ID}>
                {t.INTERVAL_CONFIG_HELPER}
            </Wrapper>

            <IntervalConfigRows
                draftIntervals={draftIntervals}
                isAdmin={isAdmin}
                oemDefaults={getManufacturerDefaults(configModel)}
                onChange={handleChangeDraft}
                onReset={handleResetDraft}
                onUnitChange={handleChangeDraftUnit}
                partLabels={getPartLabels(t)}
                parts={getModelParts(configModel)}
                strings={t}
                unitLabels={getUnitLabels(t)}
            />
        </Modal>
    );
};

export default ServiceIntervalConfigPanel;
